import HeaderView from "../../../components/admin/headerView";
import { useNavigate, useParams } from "react-router-dom";
import ListingTableHeader from "../../../components/listingTableHeader";
import { useDebouncedCallback } from "use-debounce";
import { useTranslation } from "react-i18next";
import { APP_CONFIG } from "../../../constants/constants";
import { TAdminTableRowData } from "../../../components/types";
import { TLegacyUserWithAccount } from "../../../api/types/User";
import { OrganizationUser } from "../../../api/types/Admin";
import { routeUrls } from "../../../navigation/routeUrls";
import {
  TListingQueryParams,
  TModalsState,
  setListingQueryParams,
  setModalsData,
} from "../../../api/store/commonSlice";
import { useDispatch, useSelector } from "react-redux";
import {
  localStorageKeys,
  useLocalStorage,
} from "../../../utils/localStorageItems";
import { TListingColumn } from "../../../components/modalEditListingColumns";
import React, { useEffect } from "react";
import { useOrganizationUsersQuery } from "../../../api/network/adminApiServices";
import AdminTable from "../../../components/adminTable";
import Pagination, {
  TPaginationSelected,
} from "../../../components/pagination";
import {
  AdminFormFieldSubmit,
  TSelectboxOption,
} from "../../../components/admin/formFields";
import svgIcons from "../../../assets/svg/svgIcons";
import { useSysadminLoginAsAccountUserMutation } from "../../../api/network/sysadminApiServices";
import { useLoginHandler } from "../../../hooks/loginHandler";

const all_columns = [
  "User ID",
  "Description",
  "Role",
  "Contact Name",
  "Email",
  "Action",
];

const AccountUsers = () => {
  const { accountId } = useParams<{ accountId: string }>();
  const { t } = useTranslation("translation", { keyPrefix: "admins.users" });
  const { t: tMain } = useTranslation();
  const listingQueryParams: TListingQueryParams = useSelector(
    (state: any) => state.commonReducer.listingQueryParams
  );
  const { accountUsers: orgUsersQueryParams } = listingQueryParams;

  const modalsState = useSelector(
    (state: { commonReducer: { modals: TModalsState } }) =>
      state.commonReducer.modals
  );
  const dispatch = useDispatch();
  const listingWrapperRef = React.useRef<HTMLDivElement>(null);

  // columns
  const { getLocalStorageItem } = useLocalStorage();
  const savedColumns = getLocalStorageItem(
    localStorageKeys.columns.EnterprisesUsers
  );
  const [columns, setColumns] = React.useState<TListingColumn[]>(
    all_columns.map((colItem, _) => {
      return {
        name: colItem,
        show: !!savedColumns ? savedColumns.includes(colItem) : true,
      };
    })
  );

  // preparing query params
  const debouncedSetSearchKeyword = useDebouncedCallback((value: string) => {
    dispatch(
      setListingQueryParams({
        ...listingQueryParams,
        accountUsers: {
          ...orgUsersQueryParams,
          accountid: accountId,
          page: 1,
          search: value,
        },
      })
    );
  }, 500);

  // fetching user data
  const { data: dataOrgUsers, isFetching: isFetchingOrgUsers } =
    useOrganizationUsersQuery(
      {
        ...orgUsersQueryParams,
        accountid: accountId,
        // 'search_fields' intentionally not added here as default search fields are defined in the API service
      },
      {
        skip: !accountId,
      }
    );
  const { count, next, previous, results } = dataOrgUsers || {};

  const loginHandler = useLoginHandler({ screen: "loginScreen" });
  const [sysAdminLoginAsAccountUserApiTrigger, { isLoading }] =
    useSysadminLoginAsAccountUserMutation();
  const handleLogin = (userid: string) => {
    // show loading animation
    dispatch(setModalsData({ ...modalsState, showPrimaryOverlay: true }));
    // system admin login as account user
    sysAdminLoginAsAccountUserApiTrigger({
      user_id: userid,
      account_id: accountId,
    })
      .unwrap()
      .then((response) => {
        // switch localStorage variables to another variable
        loginHandler.prepareSwitchSysadminToAccountUser();

        // pass the response to the loginSuccessHandler
        loginHandler.loginSuccessHandler(response);
        window.location.reload();
      })
      .catch(loginHandler.loginErrorHandler)
      .finally(() => {
        dispatch(setModalsData({ ...modalsState, showPrimaryOverlay: false }));
      });
  };
  // smooth scroll listing to the top on page change
  useEffect(() => {
    listingWrapperRef?.current?.scrollTo({ behavior: "smooth", top: 0 });
  }, [dataOrgUsers]);

  // preparing table data
  const tableData: TAdminTableRowData[] = !!results
    ? (results || ([] as OrganizationUser[])).map(
        (item: TLegacyUserWithAccount, index: number) => ({
          navLink: false, // no navigation required `${routeUrls.dashboardChildren.adminChildren.users}/${item.userid}`,
          cellData: [
            item?.userid,
            item?.description || "-",
            item?.roleid || "-",
            item?.contactname || "-",
            item?.contactemail || "-",
            <AdminFormFieldSubmit
              label={tMain("login")}
              variant="primary"
              customWrapperClass="w-24"
              onClick={() => handleLogin(item.userid)}
            />,
          ].filter((_, index) => columns[index].show),
          isActive: item?.isactive === 1,
        })
      )
    : [];
  const navigate = useNavigate();
  const onBackButtonClick = () => {
    navigate(routeUrls.sysadminChildren.enterprises);
  };
  return (
    <>
      <HeaderView
        title={accountId}
        showBackButton={true}
        backButtonCallback={onBackButtonClick}
      />
      <div className={`${APP_CONFIG.DES.DASH.P_HORIZ}`}>
        <ListingTableHeader
          heading={t("listing_heading")}
          searchBoxPlaceholder={t("search_placeholder")}
          searchBoxValue={orgUsersQueryParams.search}
          searchBoxOnChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            debouncedSetSearchKeyword(e.target.value)
          }
          searchBoxOnClear={() => debouncedSetSearchKeyword("")}
        />
        <AdminTable
          viewportHeightOffset={"400"}
          columns={columns.filter((item) => item.show).map((item) => item.name)}
          data={tableData}
          isLoading={isFetchingOrgUsers}
          listingQueryParams={orgUsersQueryParams}
          listingWrapperRef={listingWrapperRef}
          disabled={true}
        />
        {!isFetchingOrgUsers && (
          <Pagination
            pageSize={orgUsersQueryParams.page_size}
            handlePageSizeChange={(e: TSelectboxOption | null) => {
              dispatch(
                setListingQueryParams({
                  ...listingQueryParams,
                  accountUsers: {
                    ...orgUsersQueryParams,
                    page: 1,
                    page_size: parseInt(`${e?.value}`),
                  },
                })
              );
            }}
            totalPages={
              count ? Math.ceil(count / orgUsersQueryParams.page_size) : 1
            }
            forcePage={orgUsersQueryParams.page - 1}
            handlePageClick={(data: TPaginationSelected) => {
              dispatch(
                setListingQueryParams({
                  ...listingQueryParams,
                  accountUsers: {
                    ...orgUsersQueryParams,
                    page: data?.selected + 1,
                  },
                })
              );
            }}
            paginCurrentPage={orgUsersQueryParams.page}
            paginItemsOnCurrentPage={results?.length || 0}
            paginTotalItemsCount={count}
          />
        )}
      </div>
    </>
  );
};
export default AccountUsers;
